import { createClient } from '@supabase/supabase-js'
import type { Database } from '../types/database.types'

// Get Supabase URL and Anon Key from environment variables
// Use placeholder values during build if env vars are not set (but warn at runtime)
const configuredSupabaseUrl = import.meta.env.VITE_SUPABASE_URL
const configuredSupabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY
const supabaseUrl = configuredSupabaseUrl || 'https://placeholder.supabase.co'
const supabaseAnonKey = configuredSupabaseAnonKey || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InBsYWNlaG9sZGVyIiwicm9sZSI6ImFub24iLCJpYXQiOjE2MjAwMDAwMDAsImV4cCI6MTk3MzU3NjAwMH0.placeholder'

// Warn if environment variables are missing (only at runtime)
if (typeof window !== 'undefined') {
  if (!import.meta.env.VITE_SUPABASE_URL) {
    console.warn(
      'Missing VITE_SUPABASE_URL environment variable. Please add it to your .env file.'
    )
  }

  if (!import.meta.env.VITE_SUPABASE_ANON_KEY) {
    console.warn(
      'Missing VITE_SUPABASE_ANON_KEY environment variable. Please add it to your .env file.'
    )
  }
}

// Create Supabase client with explicit database type schema
// The type assertion ensures TypeScript uses our Database schema for type inference
export const supabase = createClient<Database>(
  supabaseUrl,
  supabaseAnonKey,
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storage: typeof window !== 'undefined' ? window.localStorage : undefined,
      storageKey: 'flowi-auth',
    },
  }
)

// Helper function to check if Supabase is configured
export const isSupabaseConfigured = (): boolean => {
  return !!(configuredSupabaseUrl && configuredSupabaseAnonKey)
}

// Helper function to get current session
export const getCurrentSession = async () => {
  const { data, error } = await supabase.auth.getSession()
  if (error) {
    console.error('Error getting session:', error)
    return null
  }
  return data.session
}

// Helper function to get current user
export const getCurrentUser = async () => {
  const session = await getCurrentSession()
  return session?.user ?? null
}
